import React, { ChangeEvent, FormEvent, useCallback } from 'react';
import { createUseStyles, useTheme } from 'react-jss';

import { Theme } from '@app/theme';
import { CheckboxBlockField, ErrorMsg } from '@app/form';
import {
  AnimatedSlidingDiv,
  Chip,
  InformationBox,
  SubmitButton,
} from '@app/core';
import { BillingMethod, CreditBalance, Feature } from '@app/api';
import { Trans, useTranslation } from 'react-i18next';
import { toEuros } from 'lib/numbers';
import { PriceTotals } from 'lib/priceComputation';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    maxWidth: '100%',
  },
  radioBlock: {
    marginTop: theme.marginBase * 3,
  },
  errorMsg: {
    marginTop: theme.marginBase * 2,
  },
  withChip: {
    flex: 1,
  },
  box: {
    marginTop: theme.marginBase,
  },
  bold: {
    fontWeight: 700,
    color: theme.colors.inkPrimary,
  },
  buttonContainer: {
    marginTop: theme.marginBase * 3,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  creditBalance: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginRight: theme.marginBase,
  },
}));

interface Props {
  onSuccess?: (billingMethod: BillingMethod) => any;
  submitButton?: React.ReactNode;
  setBillingMethod?: (flow: BillingMethod) => any;
  billingMethod: BillingMethod;
  creditBalance?: CreditBalance;
  priceTotals?: PriceTotals;
  requireInvoiceFeature?: boolean;
  context?: string;
  strictCreditBalanceCheck?: boolean;
}

export const BillingMethodForm = ({
  onSuccess,
  submitButton,
  setBillingMethod,
  billingMethod: billingMethodProps,
  creditBalance,
  priceTotals,
  requireInvoiceFeature,
  context,
  strictCreditBalanceCheck = true,
}: Props) => {
  const theme: Theme = useTheme();
  const { t } = useTranslation();
  const classes = useStyles({ theme });
  const [billingMethodState, setBillingMethodState] =
    React.useState<BillingMethod>(billingMethodProps);
  const billingMethod = setBillingMethod
    ? billingMethodProps
    : billingMethodState;

  const onSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (onSuccess) {
        onSuccess(billingMethod);
      }
    },
    [billingMethod, onSuccess],
  );

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.currentTarget;
      if (setBillingMethod) {
        setBillingMethod(value as BillingMethod);
      } else {
        setBillingMethodState(value as BillingMethod);
      }
    },
    [setBillingMethod, setBillingMethodState],
  );

  const isCreditInsufficient =
    strictCreditBalanceCheck &&
    (!creditBalance ||
      (!!creditBalance &&
        priceTotals !== undefined &&
        creditBalance.amount < priceTotals.totalNoTax));

  return (
    <form className={classes.container} onSubmit={onSubmit}>
      <CheckboxBlockField
        type="radio"
        name="billingMethod"
        className={classes.radioBlock}
        noErrorMsg
        variant="green"
        label={t('billing.billingMethod.labels.CREDIT_CARD')}
        icon="ri-bank-card-line"
        value={BillingMethod.CREDIT_CARD}
        checked={billingMethod === BillingMethod.CREDIT_CARD}
        onChange={onChange}
      />
      <AnimatedSlidingDiv isOpen={billingMethod === BillingMethod.CREDIT_CARD}>
        <InformationBox
          text={t('billing.billingMethod.info.CREDIT_CARD', { context })}
          variant="question"
          className={classes.box}
        />
      </AnimatedSlidingDiv>
      <CheckboxBlockField
        type="radio"
        name="billingMethod"
        className={classes.radioBlock}
        noErrorMsg
        variant="green"
        label={t('billing.billingMethod.labels.SEND_INVOICE')}
        icon="ri-file-list-3-line"
        value={BillingMethod.SEND_INVOICE}
        checked={billingMethod === BillingMethod.SEND_INVOICE}
        onChange={onChange}
        feature={requireInvoiceFeature ? Feature.SEND_INVOICE : undefined}
      />
      <AnimatedSlidingDiv isOpen={billingMethod === BillingMethod.SEND_INVOICE}>
        <InformationBox variant="question" className={classes.box}>
          <Trans
            i18nKey="billing.billingMethod.info.SEND_INVOICE"
            components={{ bold: <span className={classes.bold} /> }}
            context={context}
          />
        </InformationBox>
      </AnimatedSlidingDiv>
      <CheckboxBlockField
        type="radio"
        name="billingMethod"
        className={classes.radioBlock}
        noErrorMsg
        variant="green"
        icon="ri-coins-line"
        value={BillingMethod.ORG_CREDIT}
        checked={billingMethod === BillingMethod.ORG_CREDIT}
        onChange={onChange}
        disabled={isCreditInsufficient}
      >
        <div className={classes.creditBalance}>
          <span>{t('billing.billingMethod.labels.ORG_CREDIT')}</span>
          <Chip color={isCreditInsufficient ? 'red' : 'blue'}>
            {t('billing.billingMethod.info.ORG_CREDIT_AMOUNT', {
              amount: toEuros(creditBalance?.amount || 0, true),
            })}
          </Chip>
        </div>
      </CheckboxBlockField>
      <AnimatedSlidingDiv isOpen={billingMethod === BillingMethod.ORG_CREDIT}>
        <InformationBox variant="question" className={classes.box}>
          <Trans
            i18nKey="billing.billingMethod.info.ORG_CREDIT"
            components={{ bold: <span className={classes.bold} /> }}
          />
        </InformationBox>
      </AnimatedSlidingDiv>
      <ErrorMsg name="billingMethod" className={classes.errorMsg} />
      {submitButton ? (
        submitButton
      ) : (
        <div className={classes.buttonContainer}>
          <SubmitButton text={t('billing.paymentMethod.buttons.save')} />
        </div>
      )}
    </form>
  );
};
